import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Seo } from "@/components/Seo";
import { GenerationProgress } from "@/components/GenerationProgress";
import { PlanChip } from "@/components/PlanChip";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/context/AuthContext";
import { useModals } from "@/context/ModalContext";
import { apiGenerate, apiGetSearch } from "@/lib/namable";
import { NamingEngineSelector } from "@/components/NamingEngineSelector";
import { StoreBadges } from "@/components/StoreBadges";
import { Sparkles, Lock, Info, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = ["Business", "Brand", "App", "Product", "Website", "Other"];
const EXTENSIONS = [".com", ".net", ".org", ".io", ".ai", ".co"];

function newRequestId() {
  const r = (crypto?.randomUUID && crypto.randomUUID().replace(/-/g, "")) || Math.random().toString(36).slice(2);
  return "webreq_" + r;
}

export default function Generator() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { usage, user, refreshUsage, track } = useAuth();
  const { openPaywall } = useModals();

  const [category, setCategory] = useState("Business");
  const [description, setDescription] = useState(params.get("desc") || "");
  const [engines, setEngines] = useState(() => {
    const e = params.get("engines");
    return e ? e.split(",").map((s) => s.trim()).filter(Boolean) : [];
  });
  const [extension, setExtension] = useState(params.get("ext") || ".net");
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  const startRef = useRef(0);
  const autoRan = useRef(false);

  useEffect(() => {
    track("generator_viewed", {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const allowedExtensions = useMemo(() => {
    if (!usage) return [".net"];
    if (usage.plan === "guest") return usage.free_extensions || [".net"];
    return EXTENSIONS.filter((e) => (e === ".com" ? usage.allow_com : true));
  }, [usage]);

  useEffect(() => {
    // if current extension not allowed, snap to first allowed
    if (usage && !allowedExtensions.includes(extension)) setExtension(allowedExtensions[0] || ".net");
  }, [usage, allowedExtensions]); // eslint-disable-line

  const canGenerate = description.trim().length >= 8;

  const runGenerate = async () => {
    if (!canGenerate) {
      toast.error("Tell us a little more — at least 8 characters.");
      return;
    }
    setError(null);
    setGenerating(true);
    startRef.current = Date.now();
    const isGuest = !user;
    const engineMode = engines.length ? "manual" : "recommended";
    const enginesLabel = engines.length ? engines.join(",") : "recommended";
    const engineMeta = { engines: enginesLabel, engine_count: engines.length, engine_mode: engineMode };
    track(isGuest ? "free_scan_started" : "scan_started", { extension, category, ...engineMeta });
    track("generation_started", { extension, category, ...engineMeta });
    track("naming_engine_scan_started", engineMeta);
    try {
      const payload = { description: description.trim(), engines, extension, count: 25, request_id: newRequestId() };
      let data = await apiGenerate(payload);
      // handle async polling if backend returns processing
      let tries = 0;
      while (data && data.status === "processing" && tries < 22) {
        await new Promise((r) => setTimeout(r, 8000));
        data = await apiGetSearch(data.id);
        tries += 1;
      }
      const duration = Date.now() - startRef.current;
      const results = data.results || [];
      const available = results.filter((r) => r.available === true).length;
      const respMode = data.engine_mode || engineMode;
      const respEngines = (data.engines && data.engines.length ? data.engines.join(",") : enginesLabel);
      const respMeta = { engines: respEngines, engine_count: (data.engines || engines).length, engine_mode: respMode };
      track(isGuest ? "free_scan_completed" : "scan_completed", { duration_ms: duration, results: results.length, ...respMeta });
      track("generation_completed", { duration_ms: duration, results: results.length, available });
      track("naming_engine_scan_completed", { ...respMeta, duration_ms: duration });
      track("results_displayed", { results: results.length, available });
      if (results.some((r) => r.est_value_low != null)) track("domain_valuation_displayed", {});
      sessionStorage.setItem(`search:${data.id}`, JSON.stringify({ ...data, _meta: { category, duration } }));
      await refreshUsage();
      navigate(`/results/${data.id}`);
    } catch (e) {
      setGenerating(false);
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || "";
      if (status === 402) {
        const d = String(detail).toLowerCase();
        if (d.includes(".com") || d.includes("com ")) openPaywall("com_locked");
        else openPaywall("daily_limit");
        return;
      }
      setError("Generation failed. Please try again.");
    }
  };

  useEffect(() => {
    if (
      !autoRan.current &&
      params.get("auto") === "1" &&
      usage &&
      (params.get("desc") || "").trim().length >= 8 &&
      !generating
    ) {
      autoRan.current = true;
      runGenerate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usage]);

  if (generating) {
    return (
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16">
        <Seo title="Generating names… — Namable" description="Namable is generating brandable name ideas for your business." path="/generate" />
        <GenerationProgress description={description.trim()} />
      </div>
    );
  }

  return (
    <div className="max-w-[760px] mx-auto px-4 sm:px-6 py-10 sm:py-14">
      <Seo
        title="Name Generator — Describe your idea | Namable"
        description="Describe what you're building and Namable's AI generates brandable names, checks .com availability and estimates domain value."
        path="/generate"
      />

      <div className="flex items-center justify-between mb-2">
        <p className="overline text-brand">The generator</p>
        {usage && <PlanChip usage={usage} />}
      </div>
      <h1 className="font-display text-[32px] sm:text-[40px] leading-[1.05] font-extrabold tracking-[-0.03em] text-ink">
        Name your next venture.
      </h1>
      <p className="text-ink-muted text-[15px] mt-2.5 max-w-[520px]">
        Describe what you're building. Our AI finds brandable names, checks domains and estimates value.
      </p>

      {/* category */}
      <div className="mt-8">
        <label className="text-[13px] font-semibold text-ink">What are you naming?</label>
        <div className="flex flex-wrap gap-2 mt-3">
          {CATEGORIES.map((c) => (
            <button
              key={c}
              onClick={() => setCategory(c)}
              data-testid={`category-${c.toLowerCase()}`}
              className={`px-3.5 py-2 rounded-full text-[13px] font-semibold border transition-all duration-200 ${
                category === c
                  ? "bg-brand text-white border-brand shadow-glow-sm"
                  : "bg-surface text-ink-muted border-[color:var(--hairline-strong)] hover:border-brand/40 hover:text-ink"
              }`}
            >
              {c}
            </button>
          ))}
        </div>
      </div>

      {/* description */}
      <div className="mt-7">
        <label className="text-[13px] font-semibold text-ink">Tell us about it</label>
        <Textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          data-testid="description-input"
          placeholder="An online gym clothing brand focused on fitted performance clothing for men and women…"
          className="mt-3 min-h-[130px] rounded-xl text-[15px] resize-none border-[color:var(--hairline-strong)] focus-visible:ring-brand"
        />
        <p className="text-[12px] text-ink-subtle mt-1.5">
          {description.trim().length < 8
            ? `Add ${8 - description.trim().length} more characters to continue.`
            : "Great — the more detail, the better the names."}
        </p>
        <div className="mt-4" data-testid="generator-store-badges">
          <p className="text-[11px] uppercase tracking-wide text-ink-subtle mb-2">Also available on mobile</p>
          <StoreBadges />
        </div>
      </div>

      {/* naming engine */}
      <div className="mt-7">
        <NamingEngineSelector selected={engines} onChange={setEngines} onTrack={track} />
      </div>

      {/* extension */}
      <div className="mt-6">
        <label className="text-[13px] font-semibold text-ink">Domain extension</label>
        <div className="flex flex-wrap gap-2 mt-3">
          {EXTENSIONS.map((e) => {
            const locked = !allowedExtensions.includes(e);
            const active = extension === e;
            return (
              <button
                key={e}
                data-testid={`extension-${e.replace(".", "")}`}
                onClick={() => (locked ? openPaywall(e === ".com" ? "com_locked" : "com_locked") : setExtension(e))}
                className={`px-3.5 py-2 rounded-full text-[13px] font-semibold border transition-all duration-200 inline-flex items-center gap-1.5 ${
                  active
                    ? "bg-brand text-white border-brand shadow-glow-sm"
                    : locked
                    ? "bg-surface text-brand/70 border-dashed border-brand/40"
                    : "bg-surface text-ink-muted border-[color:var(--hairline-strong)] hover:border-brand/40 hover:text-ink"
                }`}
              >
                {locked && <Lock size={12} />}
                {e}
              </button>
            );
          })}
        </div>
        {usage && !usage.allow_com && (
          <button
            onClick={() => openPaywall("com_locked")}
            className="text-[12px] text-brand font-semibold mt-2.5 inline-flex items-center gap-1 hover:underline"
            data-testid="why-com-locked"
          >
            <Info size={13} /> Why is .com locked?
          </button>
        )}
      </div>

      {error && (
        <div className="mt-6 rounded-lg bg-danger-bg text-danger px-4 py-3 text-[13px] font-medium flex items-center justify-between">
          {error}
          <button onClick={runGenerate} className="inline-flex items-center gap-1 font-semibold">
            <RefreshCw size={13} /> Retry
          </button>
        </div>
      )}

      <Button
        onClick={runGenerate}
        disabled={!canGenerate}
        data-testid="generate-btn"
        className="w-full mt-8 h-14 rounded-xl bg-brand hover:bg-brand-dark text-white font-bold text-[16px] btn-glow disabled:opacity-50"
      >
        <Sparkles size={18} className="mr-2" /> Generate Names
      </Button>
      {usage && usage.plan === "guest" && (
        <p className="text-center text-[12px] text-ink-subtle mt-3">
          Free scan · no account needed · {usage.scans_remaining} of {usage.scan_allowance} left today
        </p>
      )}
    </div>
  );
}
